import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { pushNotification } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

interface UploadResponseInput {
  documentId: UUID;
  fileLocation: Apollo.FileLocationProps;
}

/** Review mutations for one task. Each resolves to whether the server accepted it. */
export function useTaskReviewActions(taskId: UUID) {
  const [setAssigneeRequired] = Apollo.useSetTaskAssigneeRequiredMutation();
  const [setAssigneeRole] = Apollo.useSetTaskAssigneeRoleMutation();
  const [requestTaskReview, { loading: requestingReview }] = Apollo.useRequestTaskReviewMutation();
  const [undoTaskReviewRequest, { loading: undoingReviewRequest }] =
    Apollo.useUndoTaskReviewRequestMutation();
  const [requestChanges, { loading: requestingChanges }] = Apollo.useRequestTaskChangesMutation();
  const [remindAssignees, { loading: reminding }] = Apollo.useRemindTaskAssigneesMutation();
  const [remindReviewers, { loading: remindingReviewers }] =
    Apollo.useRemindTaskReviewersMutation();
  const [rejectTask, { loading: rejecting }] = Apollo.useRejectTaskMutation();
  const [uploadResponse, { loading: uploading }] = Apollo.useUploadTaskResponseVersionMutation();
  const [completeForAssignee] = Apollo.useCompleteTaskForAssigneeMutation();
  const [clearTaskUpload, { loading: clearingUpload }] = Apollo.useClearTaskUploadMutation();

  const completeFor = useCallback(
    async (permissionId: UUID) => {
      const { error } = await completeForAssignee({
        variables: { input: { permissionId, taskId } },
      });
      if (error) notify(error.message, 'error');
      return !error;
    },
    [completeForAssignee, taskId]
  );

  const setRequired = useCallback(
    async (permissionId: UUID, isRequired: boolean) => {
      const { error } = await setAssigneeRequired({
        variables: { input: { isRequired, permissionId, taskId } },
      });
      return !error;
    },
    [setAssigneeRequired, taskId]
  );

  const setRole = useCallback(
    async (permissionId: UUID, role: Apollo.TaskAssigneeRole) => {
      const { error } = await setAssigneeRole({
        variables: { input: { permissionId, role, taskId } },
      });
      if (error) notify(error.message, 'error');
      return !error;
    },
    [setAssigneeRole, taskId]
  );

  const requestReview = useCallback(
    async ({
      note,
      reviewerPermissionIds = null,
    }: {
      note: string;
      /** Ask only these reviewers, in one request; the server's default targets when null. */
      reviewerPermissionIds?: readonly UUID[] | null;
    }) => {
      const { data, error } = await requestTaskReview({
        variables: {
          input: {
            note: toNote(note),
            reviewerPermissionIds: reviewerPermissionIds ? [...reviewerPermissionIds] : null,
            taskId,
          },
        },
      });
      if (error) notify(error.message, 'error');
      const warning = data?.requestTaskReview.warning;
      if (warning) notify(warning, 'warning');
      return !error;
    },
    [requestTaskReview, taskId]
  );

  const undoReviewRequest = useCallback(
    async (reviewerPermissionId: UUID | null = null) => {
      const { error } = await undoTaskReviewRequest({
        variables: { reviewerPermissionId, taskId },
      });
      if (error) notify(error.message, 'error');
      return !error;
    },
    [taskId, undoTaskReviewRequest]
  );

  const submitRequestChanges = useCallback(
    async (comment: string) => {
      const { error } = await requestChanges({ variables: { input: { comment, taskId } } });
      return !error;
    },
    [requestChanges, taskId]
  );

  const submitRemind = useCallback(
    async (note: string) => {
      const { error } = await remindAssignees({
        variables: { input: { note: toNote(note), taskId } },
      });
      if (!error) notify('Reminder sent.', 'success');
      return !error;
    },
    [remindAssignees, taskId]
  );

  const submitRemindReviewers = useCallback(
    async (note: string, reviewerPermissionId: UUID | null = null) => {
      const { error } = await remindReviewers({
        variables: { input: { note: toNote(note), reviewerPermissionId, taskId } },
      });
      if (error) notify(error.message, 'error');
      else notify('Reminder sent.', 'success');
      return !error;
    },
    [remindReviewers, taskId]
  );

  const submitReject = useCallback(
    async (reason: string) => {
      const { error } = await rejectTask({ variables: { input: { reason, taskId } } });
      return !error;
    },
    [rejectTask, taskId]
  );

  const submitUploadResponse = useCallback(
    async (input: UploadResponseInput) => {
      const { error } = await uploadResponse({
        variables: { input: { ...input, taskId } },
      });
      if (error) notify(error.message, 'error');
      else notify('File uploaded.', 'success');
      return !error;
    },
    [taskId, uploadResponse]
  );

  const clearUpload = useCallback(
    async (taskUploadId: UUID) => {
      const { error } = await clearTaskUpload({ variables: { taskUploadId } });
      if (error) notify(error.message, 'error');
      else notify('Upload removed.', 'success');
      return !error;
    },
    [clearTaskUpload]
  );

  return {
    clearUpload,
    completeFor,
    loading:
      clearingUpload ||
      requestingChanges ||
      requestingReview ||
      reminding ||
      remindingReviewers ||
      rejecting ||
      undoingReviewRequest ||
      uploading,
    reject: submitReject,
    remind: submitRemind,
    remindReviewers: submitRemindReviewers,
    requestChanges: submitRequestChanges,
    requestReview,
    setRequired,
    setRole,
    undoReviewRequest,
    uploadResponse: submitUploadResponse,
  };
}

function notify(message: string, variant: 'error' | 'success' | 'warning'): void {
  pushNotification({ key: uuid4(), message, variant });
}

// A blank note is sent as none.
function toNote(note: string): string | null {
  return note.trim() || null;
}
